import { MutateOptions, useMutation, UseMutationOptions } from "@tanstack/react-query";
import { FollowCardQuery } from "../../../api/query.types";
import { SavedShipmentResponseDataDTO } from "../../../api/response.types";

import { savedShipmentService } from "../../../api/api";
import { SeaSavedCardModel } from "../../../model/model";

type FollowCardResponse = SavedShipmentResponseDataDTO;
type FollowCardError = Error;
type FollowCardFunc = (
  data: FollowCardQuery,
  options?: Omit<MutateOptions<FollowCardResponse, FollowCardError, FollowCardQuery>, "onSuccess"> & {
    onSuccess?: (data: SeaSavedCardModel) => void;
  }
) => void;

type UseFollowCardMutationReturnType = {
  followCard: FollowCardFunc;
  followedCard: SavedShipmentResponseDataDTO | undefined;
  isCardFollowSuccess: boolean;
  isCardFollowError: boolean;
  isCardFollowLoading: boolean;
};

export const useFollowCardMutation = (
  mutationOptions?: Omit<UseMutationOptions<FollowCardResponse, FollowCardError, FollowCardQuery>, "mutationFn">
): UseFollowCardMutationReturnType => {
  const { data, mutate, isError, isPending, isSuccess } = useMutation<
    FollowCardResponse,
    FollowCardError,
    FollowCardQuery
  >({
    mutationFn: savedShipmentService.followCard,
    ...mutationOptions,
  });

  const followCard: FollowCardFunc = (data, { onSuccess, onError, ...rest } = {}) => {
    mutate(data, {
      onSuccess: (response) => {
        onSuccess?.(new SeaSavedCardModel(response));
      },
      // A failed follow leaves the search card on screen looking exactly like a
      // saved one, and it is gone on the next reload. Nothing here can show the
      // user an error - the widget has no toast - so at least say so out loud
      // instead of letting the request disappear without a trace.
      onError: (error, variables, context) => {
        // Deliberately not behind __DEV__: a follow failing silently in
        // production is the bug this exists to make visible.
        // eslint-disable-next-line no-console
        console.error("Follow failed, the shipment was not saved", error);
        onError?.(error, variables, context);
      },
      ...rest,
    });
  };

  return {
    followCard,
    followedCard: data,
    isCardFollowSuccess: isSuccess,
    isCardFollowError: isError,
    isCardFollowLoading: isPending,
  };
};
